import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { AnimatePresence, motion } from 'motion/react';
import React, { useContext, useState } from 'react';
import Button, { IconButton } from '../components/Button';
import SpringAnimation from '../components/SpringAnimation';
import Tether from '../components/Tether';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { isDraggingAtom } from '../editor/reorderGutterAtoms';
import tocIcon from '../icons/non-prefilled/16x16/tableOfContentsNew.svg';
import {
  PostLayoutSize,
  usePostLayoutSize,
} from '../utils/responsive/PostLayoutSizeProvider';
import useEventListener from '../utils/useEventListener';
import useIsPublicView from '../utils/useIsPublicView';
import * as styles from './TOCStickyControl.module.scss';
import type { TOCState } from './actionButtons/ConnectedTableOfContents';
import TableOfContents from './actionButtons/TableOfContents';

const IsExpandedContext = React.createContext(false);

const useTOCButtonAriaLabel = () => {
  const isExpanded = useContext(IsExpandedContext);
  return isExpanded ? 'Hide table of contents' : 'Show table of contents';
};

interface TOCButtonProps {
  onClick?: () => void;
  hasHeaders: boolean;
}

const TOCButton = React.forwardRef<HTMLButtonElement, TOCButtonProps>(
  ({ onClick, hasHeaders }, ref) => {
    const tocButtonAriaLabel = useTOCButtonAriaLabel();

    if (!hasHeaders) return null;

    return (
      <IconButton
        ref={ref}
        onClick={onClick}
        aria-label={tocButtonAriaLabel}
        size="md"
      >
        {tocIcon}
      </IconButton>
    );
  },
);

interface ContentContainerProps {
  children: React.ReactNode;
}

const ContentContainer = ({ children }: ContentContainerProps) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEventListener(container, 'scroll', () => {
    container?.classList.toggle(styles.scrolled, container.scrollTop > 0);
  });

  return (
    <div ref={setContainer} className={styles.content}>
      {children}
    </div>
  );
};

interface TitleProps {
  onClose?: () => void;
}

const Title = ({ onClose }: TitleProps) => {
  const tocButtonAriaLabel = useTOCButtonAriaLabel();

  return (
    <div className={styles.titleContainer}>
      <h4 className={styles.title}>Table of Contents</h4>
      {onClose && (
        <Button
          className={styles.closeAreaButton}
          aria-label={tocButtonAriaLabel}
          onClick={() => onClose()}
        />
      )}
    </div>
  );
};

interface RootProps {
  children: React.ReactNode;
  hasBanner?: boolean;
  expanded: boolean;
  alignRight?: boolean;
}

const Root = React.forwardRef<HTMLDivElement, RootProps>(
  ({ children, hasBanner, alignRight, expanded }, ref) => {
    const postLayoutSize = usePostLayoutSize();
    const isPublicView = useIsPublicView();
    const isReorderingWithDragAndDrop = useAtomValue(isDraggingAtom);

    return (
      <IsExpandedContext.Provider value={expanded}>
        <div
          ref={ref}
          className={classNames(styles.root, {
            [styles.isShowingTOC]: expanded,
            [styles.hasBanner]: hasBanner,
            [styles.isPublicView]: isPublicView,
            [styles.tablet]: postLayoutSize === PostLayoutSize.tablet,
            [styles.monitor]: postLayoutSize === PostLayoutSize.monitor,
            [styles.isReorderingWithDragAndDrop]: isReorderingWithDragAndDrop,
            [styles.alignRight]: alignRight,
          })}
        >
          {children}
          {expanded && <div className={styles.tocHoverArea} />}
        </div>
      </IsExpandedContext.Provider>
    );
  },
);

const ActionButtons = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div
    ref={ref}
    {...rest}
    className={classNames(styles.actionButtons, className)}
  >
    {children}
  </div>
));

const Container = React.forwardRef<
  HTMLElement,
  Omit<React.ComponentProps<typeof motion.nav>, 'className'>
>(({ children, ...rest }, ref) => {
  const isExpanded = useContext(IsExpandedContext);

  return (
    <motion.nav
      initial={false}
      animate={{
        opacity: isExpanded ? 1 : 0,
        x: isExpanded ? '0%' : '-50%',
      }}
      transition={{ type: 'tween', duration: 0.15 }}
      ref={ref}
      {...rest}
      className={styles.tocContainer}
    >
      {children}
    </motion.nav>
  );
});

interface TOCPopOverProps extends React.HTMLAttributes<HTMLDivElement> {
  anchorElement: Element | null;
  open: boolean;
  tocState: TOCState;
}

const TOCPopOver = React.forwardRef<HTMLDivElement, TOCPopOverProps>(
  ({ anchorElement, open, style, tocState, ...rest }, ref) => {
    return (
      <Tether
        placement="bottom-start"
        anchorElement={anchorElement}
        positionedView={
          <div
            ref={ref}
            style={{
              ...style,
              pointerEvents: open ? 'auto' : 'none',
            }}
            className={styles.popOver}
            {...rest}
          >
            <AnimatePresence>
              {open && (
                <SpringAnimation
                  direction="down"
                  className={styles.tocPopOverContainer}
                >
                  <PopOverContainer width="auto">
                    <div className={styles.tocPopOverContent}>
                      <Title />
                      <TableOfContents {...tocState} />
                    </div>
                  </PopOverContainer>
                </SpringAnimation>
              )}
            </AnimatePresence>
          </div>
        }
      />
    );
  },
);

interface ContentProps {
  tocProps: TOCState;
  onClose?: () => void;
}

const Content = React.forwardRef<HTMLElement, ContentProps>(
  ({ tocProps, onClose }, ref) => {
    return (
      <Container ref={ref}>
        <Title onClose={onClose} />
        <ContentContainer>
          <TableOfContents {...tocProps} />
        </ContentContainer>
      </Container>
    );
  },
);

const TOCStickyControl = {
  Root,
  ActionButtons,
  Content,
  PopOver: TOCPopOver,
  Button: TOCButton,
};

export default TOCStickyControl;
